import React from 'react';

export type TextVariant = 
  | 'heading' 
  | 'title' 
  | 'body' 
  | 'caption' 
  | 'error' 
  | 'muted'
  | 'label';

export type TextSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl';

export interface TextProps extends Omit<React.HTMLAttributes<HTMLElement>, 'className'> {
  variant?: TextVariant;
  size?: TextSize;
  as?: 'p' | 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label';
  mb?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  mt?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  color?: 'blue' | 'white' | 'muted' | 'error';
  opacity?: 60 | 70 | 80 | 90 | 100;
  tracking?: 'tight' | 'normal' | 'wide';
  children: React.ReactNode;
}

const variantStyles: Record<TextVariant, string> = {
  heading: 'font-bold text-white',
  title: 'font-semibold text-white',
  body: 'text-white',
  caption: 'text-white/70',
  error: 'text-[#fca5a5]',
  muted: 'text-white/50',
  label: 'text-white/50',
};

const sizeStyles: Record<TextSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
};

const defaultVariants: Record<TextVariant, TextSize> = {
  heading: '2xl',
  title: 'lg',
  body: 'sm',
  caption: 'xs',
  error: 'sm',
  muted: 'sm',
  label: 'sm',
};

const mbMap: Record<number, string> = {
  0: 'mb-0',
  1: 'mb-1',
  2: 'mb-2',
  3: 'mb-3',
  4: 'mb-4',
  5: 'mb-5',
  6: 'mb-6',
  8: 'mb-8',
  10: 'mb-10',
  12: 'mb-12',
};

const mtMap: Record<number, string> = {
  0: 'mt-0',
  1: 'mt-1',
  2: 'mt-2',
  3: 'mt-3',
  4: 'mt-4',
  5: 'mt-5',
  6: 'mt-6',
  8: 'mt-8',
  10: 'mt-10',
  12: 'mt-12',
};

const colorMap: Record<string, string> = {
  blue: 'text-[#3b82f6]',
  white: 'text-white',
  muted: 'text-white/50',
  error: 'text-[#fca5a5]',
};

const opacityMap: Record<number, string> = {
  60: 'opacity-60',
  70: 'opacity-70',
  80: 'opacity-80',
  90: 'opacity-90',
  100: 'opacity-100',
};

const trackingMap: Record<string, string> = {
  tight: 'tracking-tight',
  normal: 'tracking-normal',
  wide: 'tracking-wide',
};

export const Text: React.FC<TextProps> = ({
  variant = 'body',
  size,
  as,
  mb,
  mt,
  color,
  opacity,
  tracking,
  children,
  ...props
}) => {
  const Component = as || (variant === 'heading' ? 'h2' : 'div');
  const finalSize = size || defaultVariants[variant];
  const variantStyle = variantStyles[variant];
  const sizeStyle = sizeStyles[finalSize];
  
  const classes = [
    variantStyle,
    sizeStyle,
    mb !== undefined && mbMap[mb],
    mt !== undefined && mtMap[mt],
    color && colorMap[color],
    opacity !== undefined && opacityMap[opacity],
    tracking && trackingMap[tracking],
  ].filter(Boolean).join(' ');

  return (
    <Component
      className={classes}
      {...props}
    >
      {children}
    </Component>
  );
};

